import { Component, Input, Output, EventEmitter } from '@angular/core';

@Component({
  selector: 'app-custom-pagination',
  templateUrl: './custom-pagination.component.html',
  styleUrls: ['./custom-pagination.component.scss']
})
export class CustomPaginationComponent {
  @Input() length = 0;
  private _pageSize = 10;
  @Input() set pageSize(value: any) {
    const parsed = Number(value);
    this._pageSize = Number.isFinite(parsed) && parsed > 0 ? parsed : 10;
  }
  get pageSize(): number {
    return this._pageSize;
  }
  @Input() pageIndex = 0;
  private _pageSizeOptions = [5, 10, 25, 100];
  @Input() set pageSizeOptions(value: number[]) {
    const options = Array.isArray(value)
      ? [...new Set(value.map((x) => Number(x)).filter((x) => Number.isFinite(x) && x > 0))]
      : [5, 10, 25, 100];

    const defaultOrder = [5, 10, 25, 100];
    const allInDefaultSet = options.every((x) => defaultOrder.includes(x));
    this._pageSizeOptions = allInDefaultSet
      ? defaultOrder.filter((x) => options.includes(x))
      : options;
  }
  get pageSizeOptions(): number[] {
    return this._pageSizeOptions;
  }
  
  @Output() page = new EventEmitter<{
    previousPageIndex: number;
    pageIndex: number;
    pageSize: number;
    length: number;
  }>();

  get totalPages(): number {
    return Math.ceil(this.length / this.pageSize) || 0;
  }

  getRangeLabel(): string {
    const start = this.length === 0 ? 0 : this.pageIndex * this.pageSize + 1;
    const end = Math.min((this.pageIndex + 1) * this.pageSize, this.length);
    return `${start}–${end} of ${this.length}`;
  }

  getPageSequence(): (number | string)[] {
    const sequence: (number | string)[] = [];
    const total = this.totalPages;
    const current = this.pageIndex;

    if (total <= 6) {
      for (let i = 0; i < total; i++) {
        sequence.push(i);
      }
    } else {
      if (current < 3) {
        for (let i = 0; i < 4; i++) {
          sequence.push(i);
        }
        sequence.push('...');
        sequence.push(total - 1);
      } else if (current >= total - 3) {
        sequence.push(0);
        sequence.push('...');
        for (let i = total - 4; i < total; i++) {
          sequence.push(i);
        }
      } else {
        sequence.push(0);
        sequence.push('...');
        const start = current - 1;
        for (let i = start; i < start + 4; i++) {
          sequence.push(i);
        }
        sequence.push('...');
        sequence.push(total - 1);
      }
    }
    return sequence;
  }

  onPageClick(index: any) {
    const idx = index as number;
    if (idx !== this.pageIndex) {
      this.page.emit({
        previousPageIndex: this.pageIndex,
        pageIndex: idx,
        pageSize: this.pageSize,
        length: this.length
      });
    }
  }

  onPageSizeChange(event: any) {
    const size = parseInt(event.target.value, 10);
    this.page.emit({
      previousPageIndex: this.pageIndex,
      pageIndex: 0,
      pageSize: size,
      length: this.length
    });
  }

  onPrevClick() {
    if (this.pageIndex > 0) {
      this.page.emit({
        previousPageIndex: this.pageIndex,
        pageIndex: this.pageIndex - 1,
        pageSize: this.pageSize,
        length: this.length
      });
    }
  }

  onNextClick() {
    if (this.pageIndex < this.totalPages - 1) {
      this.page.emit({
        previousPageIndex: this.pageIndex,
        pageIndex: this.pageIndex + 1,
        pageSize: this.pageSize,
        length: this.length
      });
    }
  }
}
